import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import companiesApi, { CompanyDTO } from '../services/companies.ts';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import DetailShell from '../components/ui/DetailShell.tsx';
import StatusChip from '../components/ui/StatusChip.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { formatCnpj } from '../utils/cnpj.ts';
import { formatCurrency } from '../utils/currency.ts';

const CompanyDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { data, isLoading, isError } = useQuery<CompanyDTO>(
    ['company', id],
    () => companiesApi.get(Number(id)),
    { enabled: Boolean(id) }
  );

  const revenue = Number(data?.annualRevenue || 0);
  const profit = Number(data?.annualProfit || 0);
  const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
  const isActive = data?.status === 'active';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (isError || !data) {
    return <div className="field-error">Empresa não encontrada.</div>;
  }

  return (
    <DetailShell
      breadcrumb={`Patrimônio / Empresas / ${data.name}`}
      title={data.name}
      subtitle="Detalhe da empresa"
      statusLabel={isActive ? 'Ativa' : 'Inativa'}
      statusTone={isActive ? 'green' : 'slate'}
      backLabel="Voltar às empresas"
      onBack={() => navigate('/companies')}
      actions={
        <>
          {hasPermission('companies.update') && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(`/companies/${id}/editar`)}
            >
              Editar
            </button>
          )}
          {hasPermission('companies.delete') && (
            <button type="button" className="btn btn-danger" onClick={() => setShowDeleteModal(true)}>
              Excluir
            </button>
          )}
        </>
      }
    >
      <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="kpi">
          <div className="kpi-label">Valuation</div>
          <div className="kpi-value">{formatCurrency(Number(data.valuation || 0))}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Receita anual</div>
          <div className="kpi-value">{formatCurrency(revenue)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Lucro anual</div>
          <div className="kpi-value">{formatCurrency(profit)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Margem</div>
          <div className="kpi-value">
            {margin.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
          </div>
        </div>
      </div>

      <div
        className="grid-2"
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}
      >
        <div className="card invest-detail-card">
          <div className="card-header">
            <div className="card-title">Dados da empresa</div>
          </div>
          <dl className="detail-fields">
            <div className="span-2">
              <dt>Nome</dt>
              <dd>{data.name}</dd>
            </div>
            <div>
              <dt>CNPJ</dt>
              <dd className="mono">{data.cnpj ? formatCnpj(data.cnpj) : '—'}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>
                <StatusChip
                  label={isActive ? 'Ativa' : 'Inativa'}
                  tone={isActive ? 'green' : 'slate'}
                />
              </dd>
            </div>
          </dl>
        </div>
        <div className="card invest-detail-card">
          <div className="card-header">
            <div className="card-title">Financeiro</div>
          </div>
          <dl className="detail-fields">
            <div>
              <dt>% Lucro</dt>
              <dd>
                {Number(data.profitPercent || 0).toLocaleString('pt-BR', {
                  maximumFractionDigits: 2,
                })}
                %
              </dd>
            </div>
            <div>
              <dt>Lucro anual</dt>
              <dd>{formatCurrency(profit)}</dd>
            </div>
            <div>
              <dt>Receita anual</dt>
              <dd>{formatCurrency(revenue)}</dd>
            </div>
            <div>
              <dt>Despesas anuais</dt>
              <dd>{formatCurrency(Number(data.annualExpenses || 0))}</dd>
            </div>
            <div>
              <dt>Valuation</dt>
              <dd>{formatCurrency(Number(data.valuation || 0))}</dd>
            </div>
            <div>
              <dt>Margem</dt>
              <dd>{margin.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%</dd>
            </div>
          </dl>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Excluir empresa"
        message="Tem certeza que deseja excluir esta empresa? Esta ação não pode ser desfeita."
        onConfirm={async () => {
          if (!id) return;
          setDeleting(true);
          try {
            await companiesApi.remove(Number(id));
            queryClient.invalidateQueries(['companies']);
            navigate('/companies');
          } finally {
            setDeleting(false);
            setShowDeleteModal(false);
          }
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleting}
      />
    </DetailShell>
  );
};

export default CompanyDetail;
